/**
 * BeatSeekContestCard Component
 * A flippable card component for displaying remix contests
 */
import React, { useState } from 'react';
import { Calendar, Building2, CheckCircle, CalendarPlus, Loader2, RotateCcw } from 'lucide-react';

const BeatSeekContestCard = ({ 
  contest, 
  isOwner, 
  onEditContest,
  onDeleteContest,
  contestAddingToCalendar, 
  contestAddedToCalendar, 
  handleAddContestToCalendar 
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleFlip = (e) => {
    // Don't flip if clicking on edit button or action buttons
    if (e.target.closest('[data-no-flip]')) return;
    setIsFlipped(!isFlipped);
  };

  const categoryColor = '#a78bfa'; // Purple for contests

  return (
    <div 
      className="contest-card-container"
      data-testid={`contest-card-${contest.id}`}
      style={{
        perspective: '1000px',
        height: '320px',
        cursor: 'pointer'
      }}
      onClick={handleFlip}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div 
        className="contest-card-inner"
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {/* FRONT OF CARD */}
        <div 
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            background: isHovered 
              ? 'linear-gradient(135deg, rgba(40, 30, 60, 0.95), rgba(20, 15, 35, 0.95))'
              : 'linear-gradient(135deg, rgba(30, 20, 50, 0.9), rgba(15, 10, 30, 0.9))',
            borderRadius: '14px',
            border: isHovered ? `2px solid ${categoryColor}60` : '2px solid rgba(167, 139, 250, 0.2)',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            transform: isHovered && !isFlipped ? 'translateY(-3px)' : 'translateY(0)',
            boxShadow: isHovered ? `0 8px 24px ${categoryColor}20` : 'none',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Contest Badge */}
          <div style={{
            background: `linear-gradient(135deg, ${categoryColor}, ${categoryColor}80)`,
            color: '#fff',
            padding: '0.3rem 0.75rem',
            fontSize: '0.65rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            <span>🏆 BeatSeek Contest</span>
          </div>

          <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Title */}
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '700', 
              color: '#fff',
              margin: '0 0 0.5rem 0',
              lineHeight: '1.3'
            }}>
              {contest.title}
            </h3>

            {/* Label Name */}
            {contest.label_name && (
              <p style={{ 
                color: '#fbbf24', 
                fontSize: '0.85rem', 
                margin: '0 0 0.75rem 0',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}>
                <Building2 size={14} /> {contest.label_name}
              </p>
            )}

            {/* Prize */}
            {contest.prize && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                fontSize: '0.9rem', 
                color: '#fbbf24', 
                marginBottom: '0.5rem',
                background: 'rgba(251, 191, 36, 0.1)',
                padding: '0.4rem 0.6rem',
                borderRadius: '6px',
                width: 'fit-content'
              }}>
                🏆 {contest.prize}
              </div>
            )}

            {/* Deadline */}
            {contest.deadline && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                fontSize: '0.8rem', 
                color: 'rgba(255,255,255,0.6)', 
                marginBottom: '0.75rem' 
              }}>
                <Calendar size={14} />
                <span>Deadline: {new Date(contest.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            )}

            {/* Genres */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
              {(contest.genres || []).slice(0, 3).map((genre, index) => (
                <span key={index} style={{ 
                  background: 'rgba(167, 139, 250, 0.2)', 
                  color: '#c4b5fd', 
                  padding: '3px 8px', 
                  borderRadius: '4px', 
                  fontSize: '0.65rem' 
                }}>
                  {genre}
                </span>
              ))}
              {(contest.genres || []).length > 3 && (
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>
                  +{contest.genres.length - 3} more
                </span>
              )}
            </div>

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Owner Actions - Edit & Delete (bottom right) */}
            {isOwner && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                gap: '0.75rem',
                marginBottom: '0.5rem'
              }}>
                <span
                  data-no-flip
                  data-testid={`edit-contest-${contest.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditContest && onEditContest(contest);
                  }}
                  style={{
                    color: '#fbbf24',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    fontWeight: '500'
                  }}
                >
                  ✏️ Edit
                </span>
                <span
                  data-no-flip
                  data-testid={`delete-contest-${contest.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Delete "${contest.title}"? This cannot be undone.`)) {
                      onDeleteContest && onDeleteContest(contest.id);
                    }
                  }}
                  style={{
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    fontWeight: '500'
                  }}
                >
                  🗑️ Delete
                </span>
              </div>
            )}

            {/* Add to Calendar Button - Front */}
            {contest.deadline && (
              <button
                data-no-flip
                data-testid={`add-to-calendar-${contest.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddContestToCalendar(contest.id);
                }}
                disabled={contestAddingToCalendar === contest.id || contestAddedToCalendar.includes(contest.id)}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: contestAddedToCalendar.includes(contest.id) 
                    ? 'rgba(34, 197, 94, 0.2)' 
                    : 'rgba(167, 139, 250, 0.15)',
                  color: contestAddedToCalendar.includes(contest.id) ? '#4ade80' : '#a78bfa',
                  cursor: contestAddedToCalendar.includes(contest.id) ? 'default' : 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem',
                  marginBottom: '0.5rem',
                  width: '100%'
                }}
              >
                {contestAddedToCalendar.includes(contest.id) ? (
                  <><CheckCircle size={12} /> Added to Calendar</>
                ) : contestAddingToCalendar === contest.id ? (
                  <><Loader2 size={12} className="animate-spin" /> Adding...</>
                ) : (
                  <><CalendarPlus size={12} /> Add to Calendar</>
                )}
              </button>
            )}

            {/* View Details Hint */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '0.5rem',
              color: 'rgba(167, 139, 250, 0.7)',
              fontSize: '0.75rem',
              paddingTop: '0.5rem',
              borderTop: '1px solid rgba(167, 139, 250, 0.1)'
            }}>
              <RotateCcw size={12} />
              <span>Click to view details</span>
            </div>
          </div>
        </div>

        {/* BACK OF CARD */}
        <div 
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            background: 'linear-gradient(135deg, rgba(20, 15, 35, 0.98), rgba(40, 30, 60, 0.98))',
            borderRadius: '14px',
            border: `2px solid ${categoryColor}50`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Back Header */}
          <div style={{
            background: `linear-gradient(135deg, ${categoryColor}40, ${categoryColor}20)`,
            color: '#fff',
            padding: '0.5rem 1rem',
            fontSize: '0.85rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>{contest.title}</span>
            <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>📋 Details</span>
          </div>

          <div style={{ 
            padding: '1rem', 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'auto'
          }} className="contest-card-back-content">
            {/* Description */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                color: '#a78bfa', 
                fontSize: '0.7rem', 
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                marginBottom: '0.3rem'
              }}>
                Description
              </label>
              <p style={{ 
                color: 'rgba(255,255,255,0.8)', 
                fontSize: '0.85rem', 
                lineHeight: '1.5',
                margin: 0
              }}>
                {contest.description || 'No description provided.'}
              </p>
            </div>

            {/* Rules (if any) */}
            {contest.rules && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  color: '#fbbf24', 
                  fontSize: '0.7rem', 
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: '0.3rem'
                }}>
                  Rules
                </label>
                <p style={{ 
                  color: 'rgba(255,255,255,0.7)', 
                  fontSize: '0.8rem', 
                  lineHeight: '1.4',
                  margin: 0
                }}>
                  {contest.rules}
                </p>
              </div>
            )}

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              paddingTop: '0.75rem',
              borderTop: '1px solid rgba(167, 139, 250, 0.2)'
            }}>
              {/* Add to Calendar */}
              {contest.deadline && (
                <button
                  data-no-flip
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddContestToCalendar(contest.id);
                  }}
                  disabled={contestAddingToCalendar === contest.id || contestAddedToCalendar.includes(contest.id)}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    borderRadius: '6px',
                    border: 'none',
                    background: contestAddedToCalendar.includes(contest.id) 
                      ? 'rgba(34, 197, 94, 0.2)' 
                      : 'rgba(167, 139, 250, 0.2)',
                    color: contestAddedToCalendar.includes(contest.id) ? '#4ade80' : '#a78bfa',
                    cursor: contestAddedToCalendar.includes(contest.id) ? 'default' : 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.3rem'
                  }}
                >
                  {contestAddedToCalendar.includes(contest.id) ? (
                    <><CheckCircle size={12} /> Added</>
                  ) : contestAddingToCalendar === contest.id ? (
                    <><Loader2 size={12} className="animate-spin" /> Adding...</>
                  ) : (
                    <><CalendarPlus size={12} /> Add to Calendar</>
                  )}
                </button>
              )}
            </div>

            {/* Flip Back Hint */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '0.5rem',
              color: 'rgba(167, 139, 250, 0.5)',
              fontSize: '0.7rem',
              marginTop: '0.75rem'
            }}>
              <RotateCcw size={10} />
              <span>Click to flip back</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .contest-card-back-content::-webkit-scrollbar {
          width: 4px;
        }
        .contest-card-back-content::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 2px;
        }
        .contest-card-back-content::-webkit-scrollbar-thumb {
          background: rgba(167, 139, 250, 0.4);
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
};

export default BeatSeekContestCard;
